import { useEffect, useRef, useState } from "react";
import { trpc } from "../lib/trpc";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { CheckCircle2, FileUp, Loader2, Upload } from "lucide-react";

type Parcela = {
  id: number;
  emitenteId: number;
  pedidoId: number | null;
  numeroParcela: number | null;
  totalParcelas: number | null;
  descricao: string | null;
  valorParcela: number;
  dataVencimento: string | null;
  destinatarioNome: string | null;
  diferenca: number;
};

type Resultado = {
  linhas: Parcela[];
  total: number;
  page: number;
  pageSize: number;
};

function moeda(valor: number | null | undefined) {
  return Number(valor ?? 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function dataBr(data: string | null | undefined) {
  if (!data) return "-";
  const [ano, mes, dia] = data.slice(0, 10).split("-");
  return ano && mes && dia ? `${dia}/${mes}/${ano}` : data;
}

async function requisicao(url: string, options: RequestInit) {
  const resposta = await fetch(url, { credentials: "include", ...options });
  const texto = await resposta.text();
  let dados: any;
  try {
    dados = JSON.parse(texto);
  } catch (_) {
    console.error("[baixaGuiada] resposta inválida", { url, status: resposta.status, texto });
    throw new Error(`Resposta inválida do servidor (${resposta.status}).`);
  }
  if (!resposta.ok) throw new Error(dados.error || "Não foi possível concluir a operação.");
  return dados;
}

export default function BaixaGuiadaComprovantes() {
  const arquivoRef = useRef<HTMLInputElement>(null);
  const [emitenteId, setEmitenteId] = useState("");
  const [arrastando, setArrastando] = useState(false);
  const [analisando, setAnalisando] = useState(false);
  const [arquivo, setArquivo] = useState<{ token: string; nome: string } | null>(null);
  const [valores, setValores] = useState<number[]>([]);
  const [valorConfirmado, setValorConfirmado] = useState("");
  const [buscando, setBuscando] = useState(false);
  const [carregandoParcelas, setCarregandoParcelas] = useState(true);
  const [parcelasPendentes, setParcelasPendentes] = useState<Parcela[]>([]);
  const [resultado, setResultado] = useState<Resultado | null>(null);
  const [parcelaSelecionada, setParcelaSelecionada] = useState<Parcela | null>(null);
  const [confirmando, setConfirmando] = useState(false);

  const { data: emitentes = [] } = trpc.emitentes.list.useQuery();
  const paginaAtual = resultado?.page ?? 1;
  const totalPaginas = resultado ? Math.max(1, Math.ceil(resultado.total / resultado.pageSize)) : 1;

  useEffect(() => {
    let ativa = true;
    const inicio = performance.now();
    void (async () => {
      try {
        const dados = await requisicao("/api/baixa-guiada-comprovantes/parcelas-pendentes", { method: "GET" });
        if (!ativa) return;
        setParcelasPendentes(dados.linhas);
        console.info("[baixaGuiada] parcelas pendentes carregadas", { total: dados.linhas.length, duracaoMs: Math.round(performance.now() - inicio) });
      } catch (erro: any) {
        console.error("[baixaGuiada] erro ao carregar parcelas pendentes", erro);
        toast.error(erro.message || "Não foi possível carregar as parcelas pendentes.");
      } finally {
        if (ativa) setCarregandoParcelas(false);
      }
    })();
    return () => { ativa = false; };
  }, []);

  async function limparAnalise() {
    if (arquivo?.token) {
      try {
        await requisicao(`/api/baixa-guiada-comprovantes/temporario/${encodeURIComponent(arquivo.token)}`, { method: "DELETE" });
      } catch (_) {
        // O arquivo temporário pode já ter sido removido após a confirmação.
      }
    }
    setArquivo(null);
    setValores([]);
    setValorConfirmado("");
    setResultado(null);
    setParcelaSelecionada(null);
  }

  async function analisarArquivo(file: File) {
    if (!emitenteId) {
      toast.error("Selecione o emitente antes de enviar o comprovante.");
      return;
    }
    await limparAnalise();
    setAnalisando(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("emitenteId", emitenteId);
      const dados = await requisicao("/api/baixa-guiada-comprovantes/analisar", { method: "POST", body: formData });
      if (!dados.valores.length) {
        await requisicao(`/api/baixa-guiada-comprovantes/temporario/${encodeURIComponent(dados.token)}`, { method: "DELETE" });
        toast.error("Nenhum valor com vírgula foi localizado no comprovante.");
        return;
      }
      setArquivo({ token: dados.token, nome: dados.nome });
      setValores(dados.valores);
      setValorConfirmado(String(dados.valores[0]));
      toast.success("Comprovante analisado. Confirme o valor extraído.");
    } catch (erro: any) {
      toast.error(erro.message || "Não foi possível analisar o comprovante.");
    } finally {
      setAnalisando(false);
      if (arquivoRef.current) arquivoRef.current.value = "";
    }
  }

  async function buscarCandidatos(page: number) {
    const valor = Number(valorConfirmado);
    if (!arquivo || !emitenteId || !Number.isFinite(valor)) {
      toast.error("Confirme um valor extraído antes de buscar.");
      return;
    }
    if (carregandoParcelas) {
      toast.error("As parcelas pendentes ainda estão carregando.");
      return;
    }
    setBuscando(true);
    try {
      const doEmitente = parcelasPendentes.filter((parcela) => parcela.emitenteId === Number(emitenteId));
      const exatas = doEmitente.filter((parcela) => Number(parcela.valorParcela) === valor);
      const candidatas = exatas.length > 0
        ? exatas
        : doEmitente.filter((parcela) => Math.abs(Number(parcela.valorParcela) - valor) <= 2);
      const ordenadas = [...candidatas].sort((a, b) => {
        const diferenca = Math.abs(Number(a.valorParcela) - valor) - Math.abs(Number(b.valorParcela) - valor);
        return diferenca || String(a.dataVencimento ?? "").localeCompare(String(b.dataVencimento ?? ""));
      });
      const limite = 10;
      const dados: Resultado = {
        linhas: ordenadas.slice((page - 1) * limite, page * limite).map((parcela) => ({ ...parcela, diferenca: Math.abs(Number(parcela.valorParcela) - valor) })),
        total: ordenadas.length,
        page,
        pageSize: limite,
      };
      console.info("[baixaGuiada] filtro local concluído", { emitenteId: Number(emitenteId), valor, exatas: exatas.length, total: dados.total });
      setResultado(dados);
      if (!dados.linhas.length) toast.info("Nenhuma parcela pendente foi encontrada dentro de R$ 2,00 de tolerância.");
    } catch (erro: any) {
      console.error("[baixaGuiada] erro ao filtrar parcelas", erro);
      toast.error(erro.message || "Não foi possível buscar as parcelas.");
    } finally {
      setBuscando(false);
    }
  }

  async function confirmarBaixa() {
    if (!arquivo || !parcelaSelecionada) return;
    setConfirmando(true);
    try {
      await requisicao("/api/baixa-guiada-comprovantes/confirmar", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token: arquivo.token,
          contaReceberId: parcelaSelecionada.id,
          emitenteId: Number(emitenteId),
          valor: Number(valorConfirmado),
        }),
      });
      toast.success("Comprovante anexado e parcela baixada.");
      setParcelaSelecionada(null);
      setArquivo(null);
      setValores([]);
      setValorConfirmado("");
      setResultado(null);
    } catch (erro: any) {
      toast.error(erro.message || "Não foi possível baixar a parcela.");
    } finally {
      setConfirmando(false);
    }
  }

  return (
    <div className="p-6 space-y-5">
      <div>
        <h1 className="text-2xl font-bold">Baixa Guiada de Comprovantes</h1>
        <p className="text-sm text-muted-foreground">Envie o comprovante, confirme o valor extraído e escolha a parcela pendente.</p>
      </div>

      <Card>
        <CardContent className="py-4">
          <div className="max-w-md">
            <Label>Emitente</Label>
            <Select value={emitenteId} onValueChange={(valor) => { setEmitenteId(valor); void limparAnalise(); }}>
              <SelectTrigger className="mt-1"><SelectValue placeholder="Selecione o emitente" /></SelectTrigger>
              <SelectContent>{emitentes.map((emitente: any) => <SelectItem key={emitente.id} value={String(emitente.id)}>{emitente.nomeFantasia || emitente.razaoSocial}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-lg">Comprovante</CardTitle></CardHeader>
        <CardContent>
          <div
            className={`rounded-lg border-2 border-dashed p-8 text-center transition-colors ${arrastando ? "border-primary bg-primary/5" : "border-muted-foreground/30"}`}
            onDragOver={(event) => { event.preventDefault(); setArrastando(true); }}
            onDragLeave={() => setArrastando(false)}
            onDrop={(event) => {
              event.preventDefault();
              setArrastando(false);
              const file = event.dataTransfer.files?.[0];
              if (file) void analisarArquivo(file);
            }}
          >
            {analisando ? <div className="flex justify-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Lendo comprovante...</div> : <><FileUp className="mx-auto mb-3 h-9 w-9 text-muted-foreground" /><p className="text-sm">Arraste uma imagem ou PDF aqui</p><Button type="button" variant="outline" className="mt-3" onClick={() => arquivoRef.current?.click()}><Upload className="mr-2 h-4 w-4" />Selecionar arquivo</Button><input ref={arquivoRef} type="file" accept="image/*,.pdf,application/pdf" className="hidden" onChange={(event) => { const file = event.target.files?.[0]; if (file) void analisarArquivo(file); }} /></>}
          </div>
          {arquivo && <div className="mt-4 flex items-center justify-between rounded border p-3 text-sm"><span>{arquivo.nome}</span><Button variant="outline" size="sm" onClick={() => void limparAnalise()}>Cancelar análise</Button></div>}
        </CardContent>
      </Card>

      {arquivo && <Card>
        <CardHeader><CardTitle className="text-lg">Confirme o valor extraído</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">{valores.map((valor) => <Button key={valor} type="button" variant={valorConfirmado === String(valor) ? "default" : "outline"} onClick={() => { setValorConfirmado(String(valor)); setResultado(null); }}>{moeda(valor)}</Button>)}</div>
          <div className="flex justify-end"><Button onClick={() => void buscarCandidatos(1)} disabled={buscando || carregandoParcelas}>{carregandoParcelas ? "Carregando parcelas..." : buscando ? "Buscando..." : "Confirmar valor e buscar parcelas"}</Button></div>
        </CardContent>
      </Card>}

      {resultado && <Card>
        <CardHeader><CardTitle className="text-lg">Parcelas pendentes encontradas ({resultado.total})</CardTitle></CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto"><table className="w-full text-sm"><thead><tr className="border-b bg-muted/40"><th className="px-3 py-2 text-left">Cliente</th><th className="px-3 py-2 text-left">Parcela</th><th className="px-3 py-2 text-left">Descrição</th><th className="px-3 py-2 text-left">Vencimento</th><th className="px-3 py-2 text-right">Valor</th><th className="px-3 py-2 text-right">Ação</th></tr></thead><tbody>{resultado.linhas.map((parcela) => <tr key={parcela.id} className="border-b"><td className="px-3 py-2">{parcela.destinatarioNome || "-"}</td><td className="px-3 py-2">{parcela.numeroParcela && parcela.totalParcelas ? `${parcela.numeroParcela}/${parcela.totalParcelas}` : "-"}</td><td className="px-3 py-2">{parcela.descricao || "-"}</td><td className="px-3 py-2">{dataBr(parcela.dataVencimento)}</td><td className="px-3 py-2 text-right">{moeda(parcela.valorParcela)}</td><td className="px-3 py-2 text-right"><Button size="sm" onClick={() => setParcelaSelecionada(parcela)}>Selecionar</Button></td></tr>)}{resultado.linhas.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">Nenhuma parcela encontrada.</td></tr>}</tbody></table></div>
          {resultado.total > resultado.pageSize && <div className="flex items-center justify-between border-t p-3 text-sm"><span>Página {paginaAtual} de {totalPaginas}</span><div className="flex gap-2"><Button size="sm" variant="outline" disabled={paginaAtual === 1 || buscando} onClick={() => void buscarCandidatos(paginaAtual - 1)}>Anterior</Button><Button size="sm" variant="outline" disabled={paginaAtual === totalPaginas || buscando} onClick={() => void buscarCandidatos(paginaAtual + 1)}>Próxima</Button></div></div>}
        </CardContent>
      </Card>}

      <Dialog open={!!parcelaSelecionada} onOpenChange={(aberto) => !aberto && setParcelaSelecionada(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Deseja baixar essa parcela?</DialogTitle></DialogHeader>
          {parcelaSelecionada && <div className="space-y-2 text-sm"><p><strong>Cliente:</strong> {parcelaSelecionada.destinatarioNome || "-"}</p><p><strong>Valor da parcela:</strong> {moeda(parcelaSelecionada.valorParcela)}</p><p><strong>Valor confirmado:</strong> {moeda(Number(valorConfirmado))}</p></div>}
          <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setParcelaSelecionada(null)}>Não</Button><Button onClick={() => void confirmarBaixa()} disabled={confirmando}>{confirmando ? "Baixando..." : <><CheckCircle2 className="mr-2 h-4 w-4" />Sim</>}</Button></div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
